import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, ChevronRight, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lumis_user_token')}` });

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function PlanosPage() {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/planos`, { headers: authHeaders() })
      .then(res => setPlanos(res.data))
      .catch((err) => {
        if (err?.response?.data?.detail === 'TRIAL_EXPIRED') setPaywallOpen(true);
        else toast.error('Erro ao carregar seus planos');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {}, { headers: authHeaders() });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error('Erro ao iniciar a assinatura. Tente novamente.');
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EEF2FF] p-5 space-y-6 pb-10" data-testid="planos-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/agenda')}
          className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-xl transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-2xl font-outfit font-light text-gray-900">Meus Planos</h1>
      </div>
      <p className="text-sm text-gray-500 font-manrope">
        Cada plano de ação gerado pela IA nas suas conversas fica salvo aqui, sempre à mão.
      </p>

      {loading ? (
        <div className="text-center py-16 text-[#1E3A8A] text-2xl animate-pulse font-outfit">✦</div>
      ) : planos.length === 0 ? (
        <div className="text-center py-16 space-y-3" data-testid="planos-empty">
          <FileText size={32} className="mx-auto text-gray-400" />
          <p className="text-gray-500 font-manrope text-sm">
            Você ainda não tem planos salvos. Complete uma conversa em um módulo para gerar o primeiro.
          </p>
          <button
            onClick={() => navigate('/modules')}
            className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope text-sm transition-colors"
          >
            Explorar módulos
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="planos-list">
          {planos.map((p) => (
            <button
              key={p.id}
              data-testid={`plano-card-${p.id}`}
              onClick={() => navigate(`/planos/${p.id}`)}
              className="w-full flex items-center justify-between gap-3 text-left bg-white border border-gray-200 hover:border-[#1E3A8A]/40 rounded-2xl p-4 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-gray-900 font-manrope font-semibold text-sm truncate">{p.module_name}</p>
                <p className="text-gray-400 text-xs font-manrope mt-0.5">{fmtDate(p.created_at)}</p>
                {p.conteudo?.frase_abertura && (
                  <p className="text-gray-500 text-xs font-manrope mt-1.5 line-clamp-2">{p.conteudo.frase_abertura}</p>
                )}
              </div>
              <ChevronRight size={16} className="text-gray-400 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {paywallOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="paywall-modal">
          <div className="bg-white border border-[#1E3A8A]/20 rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
            <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={18} className="text-[#1E3A8A]" />
            </div>
            <h2 className="text-lg font-outfit font-light text-gray-900 mb-2">Seu período gratuito acabou</h2>
            <p className="text-sm text-gray-500 font-manrope mb-5">
              Assine por R$ 19,00/mês pra continuar acessando seus Planos.
            </p>
            <button
              data-testid="paywall-subscribe-btn"
              onClick={handleSubscribe}
              disabled={subscribing}
              className="w-full bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full px-6 py-2.5 font-manrope font-medium text-sm transition-colors disabled:opacity-50"
            >
              {subscribing ? 'Abrindo checkout...' : 'Assinar — R$ 19,00/mês'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
